import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Pencil, RotateCcw, Check, AlertCircle } from "lucide-react";
import api, { OCCASIONS, MATCH_CATEGORIES, CATEGORIES, PATTERNS, COLOR_FAMILIES, imageUrl } from "@/lib/api";
import { saveResult } from "@/lib/session";
import { useObjectUrl } from "@/hooks/useObjectUrl";
import { prepareImage, PHOTO_ERROR_MSG } from "@/lib/image";
import UploadZone from "@/components/UploadZone";
import AnalyzingSequence from "@/components/AnalyzingSequence";
import { Swatch } from "@/components/ColorSwatch";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function MatchMyLook() {
  const [step, setStep] = useState("upload"); // upload | preview | analyzing | confirm | matching | error
  const [file, setFile] = useState(null);
  const { url: preview, setFromFile: setPreviewFile, clear: clearPreview } = useObjectUrl();
  const [analysis, setAnalysis] = useState(null);
  const [matchOptions, setMatchOptions] = useState(MATCH_CATEGORIES);
  const [itemId, setItemId] = useState(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [category, setCategory] = useState("Complete Look");
  const [occasion, setOccasion] = useState("");
  const [issue, setIssue] = useState(null);
  const navigate = useNavigate();

  const onFile = async (f) => {
    try {
      const prepared = await prepareImage(f);
      setFile(prepared); setPreviewFile(prepared); setStep("preview");
    } catch { toast.error(PHOTO_ERROR_MSG); }
  };
  const retake = () => { setFile(null); clearPreview(); setStep("upload"); setIssue(null); };

  const analyze = async (hint) => {
    if (!file) return;
    setStep("analyzing");
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (hint) fd.append("hint", hint);
      const { data } = await api.post("/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.status === "error" || !data.analysis) {
        setIssue({ status: "error", message: data.message || "MOIRÉ couldn't read this one. Let's try again." });
        setStep("error"); return;
      }
      setAnalysis(data.analysis);
      setItemId(data.item_id);
      setMatchOptions(data.match_options || MATCH_CATEGORIES);
      setCategory((data.match_options || MATCH_CATEGORIES)[data.match_options?.length ? data.match_options.length - 1 : 0] || "Complete Look");
      setImgUrl(data.image_url ? imageUrl(data.image_url) : preview);
      if (data.status === "not_fashion" || data.status === "too_dark") {
        setIssue({ status: data.status, message: data.message }); setStep("error"); return;
      }
      setTimeout(() => setStep("confirm"), 900);
    } catch {
      setIssue({ status: "error", message: "MOIRÉ couldn't read this one. Let's try again." });
      setStep("error");
    }
  };

  const runMatch = async () => {
    setStep("matching");
    try {
      const { data } = await api.post("/match", {
        analysis, match_category: category, occasion: occasion || null, item_id: itemId,
      });
      saveResult({ ...data, image_url: imgUrl });
      setTimeout(() => navigate("/results"), 1100);
    } catch {
      toast.error("Something interrupted the styling. Please try again.");
      setStep("confirm");
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-8 py-16 md:py-24 min-h-[70vh]">
      {step === "upload" && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E] text-center">Match My Look</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#F5F2EA] text-center mt-3">What are you wearing?</h1>
          <p className="font-sans text-sm text-[#8A8170] text-center mt-4 mb-12 max-w-md mx-auto leading-relaxed">
            Photograph or upload a single piece — trousers, a shirt, a dress, shoes, a bag. MOIRÉ will do the thinking.
          </p>
          <UploadZone onFile={onFile} preview={null} onClear={retake} />
          <p className="font-sans text-[10px] tracking-wider text-[#8A8170]/60 text-center mt-8">
            Your image is uploaded securely and never shown publicly.
          </p>
        </motion.div>
      )}

      {step === "preview" && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E]">Looks good?</p>
          <h1 className="font-serif text-4xl text-[#F5F2EA] mt-3 mb-8">Use this photo</h1>
          <div className="max-w-sm mx-auto overflow-hidden border border-[#C8A97E]/40">
            <img src={preview} alt="Your item preview" className="w-full h-[400px] object-cover" />
          </div>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
            <button onClick={() => analyze()} data-testid="use-photo-btn"
              className="flex items-center justify-center gap-3 bg-[#C8A97E] text-[#0D0D0D] px-8 py-4 font-sans text-sm tracking-luxe uppercase hover:bg-[#E5C293] transition-colors">
              <Check size={16} strokeWidth={2} /> Use This Photo
            </button>
            <button onClick={retake} data-testid="retake-btn"
              className="flex items-center justify-center gap-3 border border-[#F5F2EA]/20 px-8 py-4 font-sans text-sm tracking-luxe uppercase hover:border-[#C8A97E] transition-colors">
              <RotateCcw size={16} strokeWidth={1.5} /> Choose Another
            </button>
          </div>
        </motion.div>
      )}

      {step === "analyzing" && <AnalyzingSequence stages={["Looking at the piece…", "Reading the colour…", "Studying the material…"]} />}
      {step === "matching" && <AnalyzingSequence stages={["Finding its strongest matches…", "Weighing the colours…", "Styling the look…"]} done />}

      {step === "error" && issue && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center pt-10">
          <div className="flex justify-center mb-5"><AlertCircle size={36} className="text-[#C8A97E]" strokeWidth={1.25} /></div>
          <h2 className="font-serif text-3xl text-[#F5F2EA]">A small hitch</h2>
          <p className="font-sans text-sm text-[#8A8170] mt-4 max-w-md mx-auto leading-relaxed">{issue.message}</p>
          {issue.status === "multiple" && analysis?.detected_items?.length > 0 && (
            <div className="mt-6">
              <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] mb-3">Which one are we styling?</p>
              <div className="flex flex-wrap justify-center gap-2">
                {[analysis.item, ...analysis.detected_items].map((it) => (
                  <button key={it} onClick={() => analyze(it)} data-testid={`pick-${it}`}
                    className="px-4 py-2 border border-[#F5F2EA]/15 font-sans text-xs tracking-wider uppercase hover:border-[#C8A97E]">
                    {it}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-center gap-4 mt-8">
            {analysis && issue.status !== "not_fashion" && (
              <button onClick={() => { setIssue(null); setStep("confirm"); }} data-testid="continue-anyway"
                className="border border-[#C8A97E] text-[#C8A97E] px-6 py-3 font-sans text-xs tracking-luxe uppercase hover:bg-[#C8A97E] hover:text-[#0D0D0D] transition-colors">
                Continue Anyway
              </button>
            )}
            <button onClick={retake} data-testid="error-retake"
              className="border border-[#F5F2EA]/20 px-6 py-3 font-sans text-xs tracking-luxe uppercase hover:border-[#C8A97E] transition-colors">
              Try Another Photo
            </button>
          </div>
        </motion.div>
      )}

      {step === "confirm" && analysis && (
        <ConfirmStep
          analysis={analysis} setAnalysis={setAnalysis} imgUrl={imgUrl}
          matchOptions={matchOptions} category={category} setCategory={setCategory}
          occasion={occasion} setOccasion={setOccasion}
          onContinue={runMatch} onRetake={retake}
        />
      )}
    </div>
  );
}

function ConfirmStep({ analysis, setAnalysis, imgUrl, matchOptions, category, setCategory, occasion, setOccasion, onContinue, onRetake }) {
  const [editing, setEditing] = useState(false);
  const upd = (k, v) => setAnalysis({ ...analysis, [k]: v });

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E] text-center">MOIRÉ sees</p>
      <h1 className="font-serif text-4xl text-[#F5F2EA] text-center mt-2">
        {analysis.primary_color_name} {analysis.item}
      </h1>

      <div className="grid md:grid-cols-2 gap-8 mt-10 items-center">
        {imgUrl && (
          <div className="overflow-hidden border border-[#F5F2EA]/10">
            <img src={imgUrl} alt="Your item" className="w-full h-[320px] object-cover" />
          </div>
        )}
        <div>
          {editing ? (
            <div className="space-y-5" data-testid="confirm-edit">
              <div>
                <label className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] block mb-2">Item</label>
                <Select value={CATEGORIES.find(c => c.toLowerCase() === analysis.item.toLowerCase()) || analysis.item} onValueChange={(v) => { upd("item", v); }}>
                  <SelectTrigger data-testid="edit-item" className="bg-transparent border-[#F5F2EA]/20 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#F5F2EA]/15 text-[#F5F2EA] max-h-64">
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] block mb-2">Colour</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={analysis.primary_color_hex}
                    onChange={(e) => upd("primary_color_hex", e.target.value)} data-testid="edit-color-picker"
                    className="h-10 w-12 bg-transparent border border-[#F5F2EA]/20 cursor-pointer" />
                  <Input value={analysis.primary_color_name} onChange={(e) => upd("primary_color_name", e.target.value)} data-testid="edit-color-name"
                    className="bg-transparent border-[#F5F2EA]/20 rounded-none text-[#F5F2EA]" />
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {COLOR_FAMILIES.map((f) => (
                    <button key={f.name} onClick={() => { upd("primary_color_hex", f.hex); upd("primary_color_name", f.name); }}
                      data-testid={`family-${f.name}`} title={f.name}
                      className="h-7 w-7 border border-[#F5F2EA]/15 hover:border-[#C8A97E]" style={{ backgroundColor: f.hex }} />
                  ))}
                </div>
              </div>
              <div>
                <label className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] block mb-2">Pattern</label>
                <Select value={PATTERNS.find(p => p.toLowerCase() === (analysis.pattern || "").toLowerCase()) || "Solid"} onValueChange={(v) => upd("pattern", v.toLowerCase())}>
                  <SelectTrigger data-testid="edit-pattern" className="bg-transparent border-[#F5F2EA]/20 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#F5F2EA]/15 text-[#F5F2EA]">
                    {PATTERNS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <button onClick={() => setEditing(false)} data-testid="edit-done" className="font-sans text-xs tracking-luxe uppercase text-[#C8A97E]">Done</button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <Swatch hex={analysis.primary_color_hex} size="md" />
                <div>
                  <div className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">Colour</div>
                  <div className="font-sans text-lg text-[#F5F2EA]">{analysis.primary_color_name}</div>
                </div>
              </div>
              <div className="flex gap-8 mt-5">
                <div>
                  <div className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">Pattern</div>
                  <div className="font-sans text-sm text-[#F5F2EA] capitalize">{analysis.pattern}</div>
                </div>
                <div>
                  <div className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">Style</div>
                  <div className="font-sans text-sm text-[#F5F2EA]">{analysis.style}</div>
                </div>
                {analysis.material && (
                  <div>
                    <div className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">Material</div>
                    <div className="font-sans text-sm text-[#F5F2EA] capitalize">{analysis.material}</div>
                  </div>
                )}
              </div>
              {analysis.note && (
                <p className="font-sans text-xs text-[#8A8170] italic mt-5 leading-relaxed border-l border-[#C8A97E]/40 pl-3">{analysis.note}</p>
              )}
              <div className="mt-7">
                <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] mb-3">Did we get it right?</p>
                <div className="flex gap-3">
                  <button onClick={onContinue} data-testid="confirm-yes"
                    className="font-sans text-xs tracking-luxe uppercase text-[#0D0D0D] bg-[#C8A97E] px-4 py-2 hover:bg-[#E5C293] transition-colors">
                    Yes — continue
                  </button>
                  <button onClick={() => setEditing(true)} data-testid="adjust-btn"
                    className="flex items-center gap-2 font-sans text-xs tracking-luxe uppercase text-[#F5F2EA]/70 border border-[#F5F2EA]/15 px-4 py-2 hover:border-[#C8A97E] hover:text-[#F5F2EA] transition-colors">
                    <Pencil size={12} strokeWidth={1.5} /> Adjust
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="gold-line my-12" />

      <h2 className="font-serif text-3xl text-[#F5F2EA] text-center">What are we matching?</h2>
      <div className="flex flex-wrap justify-center gap-3 mt-6">
        {matchOptions.map((c) => (
          <button key={c} onClick={() => setCategory(c)} data-testid={`cat-${c.replace(/[^a-z]/gi, "").toLowerCase()}`}
            className={`px-5 py-2.5 font-sans text-xs tracking-luxe uppercase border transition-colors ${
              category === c ? "border-[#C8A97E] bg-[#C8A97E] text-[#0D0D0D]" : "border-[#F5F2EA]/15 text-[#F5F2EA]/80 hover:border-[#C8A97E]"}`}>
            {c}
          </button>
        ))}
      </div>

      <div className="max-w-xs mx-auto mt-10">
        <label className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] block text-center mb-3">Where are you going? (optional)</label>
        <Select value={occasion || "none"} onValueChange={(v) => setOccasion(v === "none" ? "" : v)}>
          <SelectTrigger data-testid="occasion-select" className="bg-transparent border-[#F5F2EA]/15 rounded-none font-sans text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-[#F5F2EA]/15 text-[#F5F2EA]">
            <SelectItem value="none">Just Match It</SelectItem>
            {OCCASIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mt-12">
        <button onClick={onContinue} data-testid="find-matches-btn"
          className="flex items-center gap-3 bg-[#C8A97E] text-[#0D0D0D] px-10 py-4 font-sans text-sm tracking-luxe uppercase hover:bg-[#E5C293] transition-colors">
          Find My Matches <ArrowRight size={16} strokeWidth={2} />
        </button>
        <button onClick={onRetake} className="font-sans text-xs tracking-luxe uppercase text-[#8A8170] hover:text-[#C8A97E]">
          Start over
        </button>
      </div>
    </motion.div>
  );
}
